export const KNOWN_TIER_KEYS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;
export type TierKey = (typeof KNOWN_TIER_KEYS)[number];

type TierBand = {
  tier: TierKey;
  usersFrom: number;
  usersTo: number | null; // exclusive upper bound; null = no cap
  aiCreditsPerUser: number;
  integrationsCount: number;
};

// Connected-system-user band thresholds, AI credit rate, and included-integrations count per
// tier, per the v5.8 pricing model (parent epic NEX-11651). $/user from the same table is out
// of scope here (billing math, not something Loop calculates or enforces).
//
// AI credit allocation is banded/marginal, like a tax bracket: the first 250 users are credited
// at Tier A's rate, the next 750 (251-1000) at Tier B's rate, and so on - not a single flat rate
// applied to the whole count. integrationsCount stays a flat per-tier lookup instead (it's an
// account-level cap, not a per-user quantity, so it doesn't get summed across bands).
const TIER_BANDS: TierBand[] = [
  { tier: 'A', usersFrom: 0, usersTo: 251, aiCreditsPerUser: 100, integrationsCount: 1 },
  { tier: 'B', usersFrom: 251, usersTo: 1_001, aiCreditsPerUser: 90, integrationsCount: 2 },
  { tier: 'C', usersFrom: 1_001, usersTo: 3_001, aiCreditsPerUser: 80, integrationsCount: 4 },
  { tier: 'D', usersFrom: 3_001, usersTo: 10_001, aiCreditsPerUser: 70, integrationsCount: 6 },
  { tier: 'E', usersFrom: 10_001, usersTo: 25_001, aiCreditsPerUser: 60, integrationsCount: 8 },
  {
    tier: 'F',
    usersFrom: 25_001,
    usersTo: 50_001,
    aiCreditsPerUser: 50,
    integrationsCount: 10,
  },
  {
    tier: 'G',
    usersFrom: 50_001,
    usersTo: 100_001,
    aiCreditsPerUser: 40,
    integrationsCount: 15,
  },
  { tier: 'H', usersFrom: 100_001, usersTo: null, aiCreditsPerUser: 30, integrationsCount: 20 },
];

export const resolveTier = (connectedSystemUserCount: number): TierKey => {
  const band = TIER_BANDS.find(
    (b) =>
      connectedSystemUserCount >= b.usersFrom &&
      (b.usersTo === null || connectedSystemUserCount < b.usersTo),
  );
  // TIER_BANDS covers [0, Infinity) with no gaps, so this is unreachable for any
  // non-negative count - fall back to the top tier rather than throw.
  return band?.tier ?? 'H';
};

export const computeAiCreditAllocation = (connectedSystemUserCount: number): number => {
  let total = 0;
  let remaining = connectedSystemUserCount;

  for (const band of TIER_BANDS) {
    if (remaining <= 0) {
      break;
    }

    // Tier A's usersFrom is 0 purely so resolveTier(0) resolves to 'A' - the first
    // chargeable user is #1, so band capacity is measured from 1, not from usersFrom.
    const bandCapacity =
      band.usersTo === null ? remaining : band.usersTo - Math.max(band.usersFrom, 1);
    const usersInBand = Math.min(remaining, bandCapacity);

    total += usersInBand * band.aiCreditsPerUser;
    remaining -= usersInBand;
  }

  return total;
};

const INTEGRATIONS_COUNT_BY_TIER: Record<TierKey, number> = Object.fromEntries(
  TIER_BANDS.map((band) => [band.tier, band.integrationsCount]),
) as Record<TierKey, number>;

export const getIntegrationsCount = (tier: TierKey): number => INTEGRATIONS_COUNT_BY_TIER[tier];
